import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import analyticsApiClient from '../services/analytics';
import { useStateSelector } from '../store/';
import { selectAppOperation } from '../reducers/selectors';
import { sendEventToOptimizely } from '../optimizely';

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

interface FeedbackContent {
  content: FeedbackDescription[];
}

interface FeedbackDescription {
  description?: string;
  descriptionKey?: string;
  textEnabled?: boolean;
  translationKey?: string;
  parent?: string;
  title?: string;
  backButton?: string;
  completedMessage?: string;
  completedTitle?: string;
  sendFeedback?: string;
  textFeedbackDescription?: string;
  textFeedbackSubTitle?: string;
  textFeedbackTitle?: string;
  warningMessage?: string;
  contactUsLink?: string;
}

interface FeedbackEventDetail {
  data: Record<string, unknown>;
  timestamp: Date;
}

// Feedback Fragment (ESI) from Purchase History, to measure customer satisfaction
const CustomerFeedbackSurvey: React.FC = () => {
  const { t } = useTranslation();
  const { sendEventToEpisod } = analyticsApiClient();
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const appOperation = useStateSelector(selectAppOperation);

  useEffect(() => {
    const onSubmitHandler = (event: Event) => {
      const customEvent = event as CustomEvent<FeedbackEventDetail>;

      const { label, action } = customEvent.detail.data;
      if (label && action) {
        const eventName = `customer_satisfaction_${label}_${serviceId}_${appOperation}`;
        sendEventToEpisod(eventName);
        sendEventToOptimizely(eventName);
      }
    };

    const addSurveySubmitListener = () => {
      document.body.addEventListener(
        'cssom-feedback-submit',
        onSubmitHandler as EventListener,
      );
    };

    addSurveySubmitListener();

    return () => {
      document.body.removeEventListener(
        'cssom-feedback-submit',
        onSubmitHandler as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSurveyTexts = () => {
    return [
      {
        content: [
          {
            completedTitle: t('components.feedbackSurvey.completedTitle'),
            completedMessage: t('components.feedbackSurvey.completedMessage'),
          },
        ],
      },
    ];
  };

  const surveyTexts: FeedbackContent[] = getSurveyTexts();

  return (
    <div role="group" aria-labelledby="feedback-survey-title">
      <VisuallyHidden id="feedback-survey-title" aria-hidden="true">
        {t('components.feedbackSurvey.title')}
      </VisuallyHidden>
      <div
        data-cssom-feedback-id="boka-customer-feedback-survey"
        data-feedback-title={t('components.feedbackSurvey.title')}
        data-feedback-option={JSON.stringify(surveyTexts)}
        data-feedback-page="appointment" // GA event_action
        data-feedback-endpoint="#" // Required to be # according to P.H. team
      />
    </div>
  );
};

export default CustomerFeedbackSurvey;
